import Dexie from 'dexie'
import minBy from 'lodash/minBy'
import noop from 'lodash/noop'
import { Vimeo } from 'vimeo'

import to from '@/helpers/to'
import config from './config.json'

class VideoManager {
  static DATABASE_NAME = 'vino_video_database'

  static DATABASE_VERSION = 1

  static OBJECT_STORE_NAME = 'videos'

  static INDEX_NAME = 'video'

  constructor() {
    /**
     * @type {Dexie}
     */
    this.db = null
    /**
     * @type {Dexie.Table}
     */
    this.dbTable = null
    this.vimeo = new Vimeo(config.client_id, config.client_secret, config.access_token)
    this.initPromise = this.init()

    this.vimeoRequest = options =>
      new Promise((resolve, reject) => {
        this.vimeo.request(options, (error, result) => {
          if (error) return reject(error)

          resolve(result)
        })
      })
  }

  /**
   * Initialize the video storage manager
   * @returns {void}
   */
  async init() {
    const db = new Dexie(VideoManager.DATABASE_NAME)

    // Define the version and the indexed properties
    db.version(VideoManager.DATABASE_VERSION).stores({
      [VideoManager.OBJECT_STORE_NAME]: 'name'
    })

    return new Promise((resolve, reject) => {
      db.open()
        .then(() => {
          this.db = db
          this.dbTable = db[VideoManager.OBJECT_STORE_NAME]

          resolve(this.db)
        })
        .catch(reject)
    })
  }

  /**
   * Test if a video is stored in the indexeddb
   * @param {string} name
   */
  async has(name) {
    await this.initPromise

    const [error, result] = await to(this.getVideo(name))

    if (error || !result) return false

    return true
  }

  /**
   * Store a video
   * @param {string} name
   * @param {Promise} blob
   */
  async storeVideo(name, blob) {
    await this.initPromise

    await this.db.transaction('rw', this.dbTable, async () => {
      await this.dbTable.put({ name, video: blob })
    })
  }

  /**
   * Get a video from the local indexeddb
   * @param {string} name
   * @returns {Promise<Blob | null>}
   */
  async getVideo(name) {
    await this.initPromise

    try {
      const { video } = await this.dbTable.get({ name })

      return video
    } catch (error) {
      return null
    }
  }

  /**
   * Fetch a video file and retrieve it as a blob
   * @param {string} name
   * @param {string} vimeoId
   * @param {boolean} [force]
   * @param {Function} onProgress
   * @returns {Blob}
   */
  async fetchVideo(name, vimeoId, force = false, onProgress = null) {
    await this.initPromise

    if (!force) {
      const [existError, existVideo] = await to(this.getVideo(name))

      if (existVideo && !existError) return existVideo
    }

    const { files } = await this.vimeoRequest({
      method: 'GET',
      path: `videos/${vimeoId}`
    })

    const smallestFileUri = minBy(files, file => file.size).link
    const blob = await this._fetchVideoWithMonitor(smallestFileUri, onProgress)

    return this.storeVideo(name, blob)
  }

  /**
   * Delete the given vimeoId from the indexeddb
   * @param {string} vimeoId
   * @returns {Promise}
   */
  async deleteVideo(vimeoId) {
    await this.initPromise

    this.db.transaction('rw', this.dbTable, async () => {
      await this.dbTable.delete(vimeoId)
    })
  }

  /**
   * Bulk delete the given vimeoIds from the indexeddb
   * @param {string[]} vimeoIds
   * @returns {Promise}
   */
  async bulkDeleteVideo(vimeoIds) {
    await this.initPromise

    this.db.transaction('rw', this.dbTable, async () => {
      await this.dbTable.bulkDelete(vimeoIds)
    })
  }

  /**
   * @param {string} url
   * @param {Function} monitor
   */
  async _fetchVideoWithMonitor(url, monitor) {
    const response = await fetch(url)
    const callback = typeof monitor === 'function' ? monitor : noop

    const contentSize = Number(response.headers.get('Content-Length'))
    const reader = response.body.getReader()

    let received = 0
    const chunks = []

    // eslint-disable-next-line
    while (true) {
      const { done, value } = await reader.read()

      if (done) {
        callback(1)
        reader.releaseLock()
        break
      }

      chunks.push(value)

      received += value.length

      callback(received / contentSize)
    }

    return new Blob(chunks, { type: 'video/mp4' })
  }

  /**
   * Extract vimeo id from a lesson
   * @param {Lesson} lesson
   */
  getVimeoIdFromLesson(lesson) {
    if (!lesson.videoUrl) return null

    const videoUrlParts = /\/video\/(\d+)/.exec(lesson.videoUrl)

    if (!Array.isArray(videoUrlParts)) return null

    return videoUrlParts[1]
  }
}

export default VideoManager
